import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import Invoice from "./Invoice";
import PdfDocDefault from "../../assets/svg/PdfDocDefault";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Snackbar,
  Alert
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { SelectInvoiceTemplate } from "./SelectInvoiceTemplate";
import { getInvoiceFileName } from "../../utils/invoiceFilename";

// Secure email validation function that avoids ReDoS vulnerabilities
const validateEmailFormat = (email) => {
  // Simple validation that avoids regex backtracking issues
  // This is a basic check that's secure against ReDoS
  if (!email || typeof email !== "string") return false;

  // Trim whitespace
  email = email.trim();

  // Check for basic email structure without using complex regex
  const parts = email.split("@");
  if (parts.length !== 2) return false;

  const [localPart, domain] = parts;

  // Check local part
  if (!localPart || localPart.length > 64) return false;

  // Check domain
  if (!domain || domain.length > 255) return false;

  // Check for at least one dot in domain
  if (!domain.includes(".")) return false;

  // Check domain parts
  const domainParts = domain.split(".");
  if (domainParts.some((part) => !part || part.length > 63)) return false;

  return true;
};

const PDFDialog = ({ onClose, handleSendEmailClick, row }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [emails, setEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [subjectError, setSubjectError] = useState("");
  const [bodyError, setBodyError] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Track if email has been entered
  const [emailEntered, setEmailEntered] = useState(false);

  // Track selected template and pdf url from SelectInvoiceTemplate
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);

  useEffect(() => {
    // Only validate email if the user has entered something
    if (emailEntered) {
      validateEmails(emails);
    }
  }, [emails, emailEntered]);

  const validateEmails = (emailString) => {
    // If email is empty and user has entered something, show required error
    if (!emailString.trim() && emailEntered) {
      setIsValidEmail(false);
      setEmailError("Email is required");
      return;
    }

    // If email is not empty, validate the format
    if (emailString.trim()) {
      const emailArray = emailString.split(",").map((email) => email.trim());
      const isValid = emailArray.every((email) => validateEmailFormat(email));
      setIsValidEmail(isValid);

      if (!isValid) {
        setEmailError("Please enter valid email addresses");
      } else {
        setEmailError("");
      }
    } else {
      // If email is empty and user has entered something, show required error
      setIsValidEmail(false);
      setEmailError("Email is required");
    }
  };

  const validateSubject = () => {
    if (!subject.trim()) {
      setSubjectError("Subject is required");
      return false;
    }
    setSubjectError("");
    return true;
  };

  const validateBody = () => {
    if (!body.trim()) {
      setBodyError("Email body is required");
      return false;
    }
    setBodyError("");
    return true;
  };

  const isFormValid = () => {
    return (
      emails.trim() !== "" &&
      subject.trim() !== "" &&
      body.trim() !== "" &&
      isValidEmail
    );
  };

  const fileName = getInvoiceFileName(row);
  console.log("Final File Name:", fileName);

  const handleSend = async () => {
    if (isSending) return; // Prevent double execution
    setIsSending(true);
    setEmailEntered(true);
    if (!validateSubject() || !validateBody() || !isValidEmail || !pdfBlob) {
      setIsSending(false);
      return;
    }

    try {
      const pdfFile = new File([pdfBlob], fileName, {
        type: "application/pdf"
      });

      const result = {
        invoice_pdf: pdfFile,
        to_email: emails,
        email_subject: subject,
        email_body: body
      };
      if (result) {
        handleSendEmailClick(result);
        setShowSuccess(true); // Show success Snackbar
      }
    } catch (error) {
      console.error("Error sending email:", error);
    } finally {
      // Don't set isSending to false here; do it after Snackbar closes
    }
  };

  const generatePDFBlob = async () => {
    const pdfBlob = await pdf(<Invoice row={row} />).toBlob();
    console.log(pdfBlob);
    return pdfBlob;
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "calc(100vw - 32px)",
          height: "calc(100vh - 32px)",
          margin: "16px",
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
      sx={{
        "& .MuiDialog-paper": {
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          background: "white"
        }
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#f5f5f5",
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px"
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Send Invoice Email
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          aria-label="close"
          sx={{
            color: "rgba(0, 0, 0, 0.54)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "16px" }}>
        <Box sx={{ mb: 2, mt: 2 }}>
          <TextField
            label="Recipient Emails"
            placeholder="Enter email addresses (comma-separated)"
            fullWidth
            size="small"
            value={emails}
            onChange={(e) => {
              setEmails(e.target.value);
              setEmailEntered(true);
            }}
            error={!isValidEmail && emailEntered}
            helperText={emailEntered ? emailError : ""}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main"
                }
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Subject"
            placeholder="Enter email subject"
            fullWidth
            size="small"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={!!subjectError}
            helperText={subjectError}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main"
                }
              }
            }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            label="Email Body"
            placeholder="Enter your message"
            fullWidth
            size="small"
            multiline
            value={body}
            rows={4}
            onChange={(e) => setBody(e.target.value)}
            error={!!bodyError}
            helperText={bodyError}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "primary.main"
                }
              }
            }}
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box
          sx={{
            p: 1.5,
            backgroundColor: "#f9f9f9",
            borderRadius: 1,
            border: "1px solid #e0e0e0"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <AttachFileIcon
              sx={{ mr: 1, color: "primary.main", fontSize: "1.2rem" }}
            />
            <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
              Select Invoice Template to Attach
            </Typography>
          </Box>
          <SelectInvoiceTemplate
            row={row}
            onPdfBlobChange={(blob, templateId) => {
              setPdfUrl(blob ? URL.createObjectURL(blob) : "");
              setPdfBlob(blob);
              setSelectedTemplateId(templateId || null);
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "12px 16px",
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#f5f5f5"
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          size="small"
          sx={{
            mr: 1,
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          color="primary"
          disabled={
            !isFormValid() || isSending || !selectedTemplateId || !pdfUrl
          }
          variant="contained"
          size="small"
          startIcon={
            isSending ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isSending ? "Sending..." : "Send"}
        </Button>
        <Snackbar
          open={showSuccess}
          autoHideDuration={2000}
          onClose={() => {
            setShowSuccess(false);
            setIsSending(false);
            onClose(); // Close the modal after email sent
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity="success" sx={{ width: "100%" }}>
            Email sent!
          </Alert>
        </Snackbar>
      </DialogActions>
    </Dialog>
  );
};

export default PDFDialog;
